import { httpsCallable } from 'firebase/functions';
import type { CoachMessage, CoachQuota, UserDoc } from '@erectus/shared';
import { firebase } from './firebase.js';
import { listBreakLog, listExerciseLog, listSessions, listSnackLog, type ExerciseLogEntry } from './repo.js';
import type { BreakLogEntry, SnackLogEntry } from '@erectus/shared';
import { appConfig } from './remoteConfig.js';
import { deviceClass, effectivePoseModel, isNative } from './device.js';
import { provisional, warm } from './warmCache.js';
import { loadCoachThread, todayKey } from '@/features/coach/coachThread.js';
import { loadLandmarker } from '@/pose/landmarker.js';
import { useAuth } from '@/stores/auth.js';

/**
 * What the splash prepares while it plays, and the one place each screen's
 * data is fetched — the screen and the splash share the key and the loader,
 * so they always meet on the same cache entry.
 *
 * The splash waits only for Progress — the session history, the screen the
 * app opens on. Everything else loads right after it leaves, in the background:
 *   Session   — the pose model (the slow part of opening the camera), once
 *               the app is idle
 *   Darwin    — the conversation and today's allowance (`coachQuota` is a
 *               callable; a cold start alone can take seconds)
 *   Exercises — nothing: the library ships in the bundle
 *   Settings  — nothing: it reads the user doc auth already loaded
 *
 * The camera itself is never opened here. It turns on only when the user picks
 * a session (CLAUDE.md capture invariant 1) — and the platform would show its
 * recording indicator during the splash otherwise.
 */
const SLOW_NETWORK_MS = 12_000;

export const warmKeys = {
  sessions: (uid: string) => `sessions:${uid}`,
  // Today's window only; the key rolls over at local midnight with the allowance.
  coachThread: (uid: string) => `coachThread:${uid}:${todayKey()}`,
  coachQuota: (uid: string) => `coachQuota:${uid}`,
  exerciseLog: (uid: string) => `exerciseLog:${uid}`,
  breakLog: (uid: string) => `breakLog:${uid}`,
  snackLog: (uid: string) => `snackLog:${uid}`,
};

/** Breaks and snacks: the longest program (6 weeks) plus a margin. */
const HABIT_LOG_DAYS = 56;

/** How far back the Activity calendar reads the exercise log. */
const EXERCISE_LOG_DAYS = 120;

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export const loaders = {
  // Offline, Firestore can wait indefinitely for the server; say so instead.
  sessions: async (uid: string) => {
    const { rows, provisional: fromCache } = await withTimeout(
      listSessions(uid),
      SLOW_NETWORK_MS,
      'Your history is taking too long to load. Check your connection.',
    );
    return fromCache ? provisional(rows) : rows;
  },
  coachThread: (uid: string): Promise<CoachMessage[]> => loadCoachThread(uid),
  exerciseLog: (uid: string): Promise<ExerciseLogEntry[]> =>
    listExerciseLog(uid, Date.now() - EXERCISE_LOG_DAYS * 24 * 60 * 60 * 1000),
  breakLog: (uid: string): Promise<BreakLogEntry[]> =>
    listBreakLog(uid, Date.now() - HABIT_LOG_DAYS * 24 * 60 * 60 * 1000),
  snackLog: (uid: string): Promise<SnackLogEntry[]> =>
    listSnackLog(uid, Date.now() - HABIT_LOG_DAYS * 24 * 60 * 60 * 1000),
  coachQuota: async (): Promise<CoachQuota> => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const call = httpsCallable<{ tz: string }, CoachQuota>(firebase().functions, 'coachQuota');
    return (await call({ tz })).data;
  },
};

/** The first screen's data. The splash waits for this (capped). */
export async function warmFirstScreen(uid: string): Promise<void> {
  await Promise.all([
    warm(warmKeys.sessions(uid), () => loaders.sessions(uid)),
    // Progress's Activity calendar marks exercise days too.
    warm(warmKeys.exerciseLog(uid), () => loaders.exerciseLog(uid)),
    // Progress's Today card: breaks and snacks logged today.
    warm(warmKeys.breakLog(uid), () => loaders.breakLog(uid)),
    warm(warmKeys.snackLog(uid), () => loaders.snackLog(uid)),
  ]);
}

/** The other screens, after the splash: network only, nothing on screen stalls. */
export function warmOtherScreens(uid: string): void {
  void warm(warmKeys.coachThread(uid), () => loaders.coachThread(uid));
  void warm(warmKeys.coachQuota(uid), () => loaders.coachQuota());
}

/**
 * Right after the splash: Darwin's data at once (network), then the pose
 * model once the first screen has settled and the main thread is idle. A
 * session opened before that joins the same in-flight load.
 */
export function warmAfterLaunch(): void {
  const { user, userDoc } = useAuth.getState();
  if (!user) return;
  warmOtherScreens(user.uid);
  const idle =
    (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
      .requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));
  window.setTimeout(() => idle(() => void warmPoseModel(userDoc), { timeout: 3000 }), 800);
}

/**
 * Initialise the pose model and run it once on a blank frame, so the first
 * real frame does not pay for shader compilation either. Native only: on the
 * web this would download ~17 MB on every page load, for visitors who may
 * never open the camera.
 *
 * This holds the main thread for a few seconds, so it runs when the app is
 * idle after launch (`warmAfterLaunch`), never during the splash.
 */
export async function warmPoseModel(userDoc: UserDoc | null): Promise<void> {
  if (!isNative() || !userDoc?.onboardingCompletedAt) return;
  const model = effectivePoseModel(appConfig().poseModel, deviceClass());
  const t0 = performance.now();
  try {
    const loaded = await loadLandmarker(model);
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    canvas.getContext('2d')?.fillRect(0, 0, 64, 64);
    // A timestamp the real session clock (performance.now) will always exceed.
    loaded.landmarker.detectForVideo(canvas, 1);
    console.info(
      `[warmup] pose model ${loaded.model}/${loaded.delegate} ready in ${Math.round(performance.now() - t0)}ms`,
    );
  } catch {
    // The capture screen loads it again and reports the error there.
  }
}
